import { useEffect, useRef, useState } from 'react'

import { createCancelTokenSource, isCancel } from 'common/api'
import ConfirmationModal from 'common/components/confirmationModal'
import { DetailsPanelButton as Button } from 'common/components/detailsPanel'
import { errorToast, successToast } from 'common/components/toastNotification'
import { Archive } from 'common/icons'
import useUpdateProjectActionMutation from 'features/projects/mutations/useUpdateProjectActionMutation'

interface Props {
  accountId: string
  projectId: string
  isExpanded: boolean
}

const ArchiveButton = ({ isExpanded, accountId, projectId }: Props) => {
  const mutation = useUpdateProjectActionMutation()
  const sourceRef = useRef(createCancelTokenSource())

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  useEffect(() => {
    const source = sourceRef.current
    return () => source.cancel()
  }, [])

  const archive = async () => {
    const source = sourceRef.current

    try {
      await mutation.mutateAsync({
        action: 'archived',
        value: true,
        accountId,
        projectId,
        source,
      })
      successToast('Project archived successfully')
      setIsModalOpen(false)
    } catch (error) {
      if (!isCancel(error)) {
        errorToast('Failed to archive project')
      }
    }
  }

  return (
    <>
      <Button
        isExpanded={isExpanded}
        data-cy="archive-project-button"
        title="Archive project"
        onClick={() => setIsModalOpen(true)}
        icon={<Archive />}
      />
      <ConfirmationModal
        data-cy="archive-project-confirm-modal"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onConfirm={archive}
        title="Archive project?"
        confirmationText="You will no longer be able to use it to create new campaigns."
        confirmButtonText="Yes, archive it!"
        isLoading={mutation.isLoading}
      />
    </>
  )
}

export default ArchiveButton
